// src/layouts/bottlenecks/component/BottlenecksMap.js
import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import { Box, FormControlLabel, Switch } from "@mui/material";
import MDTypography from "components/MDTypography";
import { useTranslation } from "react-i18next";

import config from "config";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;

const bottleneckClasses = (t) => [
  { id: 0, label: t("bottlenecks.classes.freeFlow"), color: "#12B51A" }, // green
  { id: 3, label: t("bottlenecks.classes.deactivation"), color: "#FFEB3B" }, // yellow
  { id: 2, label: t("bottlenecks.classes.activation"), color: "#E98500" }, // orange
  { id: 1, label: t("bottlenecks.classes.congested"), color: "#C74811" }, // red
];

// Helper: compute bounds from LineString / MultiLineString
function computeBounds(geojson) {
  const bounds = new mapboxgl.LngLatBounds();

  if (!geojson?.features?.length) return null;

  geojson.features.forEach((feat) => {
    const geom = feat.geometry;
    if (!geom) return;

    if (geom.type === "LineString") {
      geom.coordinates.forEach((c) => bounds.extend(c));
    } else if (geom.type === "MultiLineString") {
      geom.coordinates.forEach((line) => {
        line.forEach((c) => bounds.extend(c));
      });
    }
  });

  return bounds.isEmpty() ? null : bounds;
}

// Hover / click interactions for the layer
function addBottlenecksInteractivity(map, t) {
  // Guard: bind interactivity ONLY once per map instance
  if (map.__bottlenecksInteractivityBound) {
    // console.log("Bottlenecks interactivity already bound, skipping.");
    return;
  }
  map.__bottlenecksInteractivityBound = true;

  const hoverPopup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  });

  const clickPopup = new mapboxgl.Popup({
    className: "my-custom-popup",
    closeButton: true,
    closeOnClick: false,
  });

  let hoveredId = null;
  let clickedId = null;

  // Hover
  map.on("mouseenter", "bottlenecks-layer", (e) => {
    map.getCanvas().style.cursor = "pointer";

    const feature = e.features && e.features[0];
    if (!feature) return;

    const id = feature.id;

    if (hoveredId !== null && hoveredId !== id) {
      map.setFeatureState(
        { source: "bottlenecks-source", id: hoveredId },
        { hover: false }
      );
    }

    hoveredId = id;

    map.setFeatureState({ source: "bottlenecks-source", id }, { hover: true });

    const props = feature.properties || {};
    const label = props.label || "";
    const date = props.date || null;


    const html = `
      <div>
         <p><strong>${label}</strong></p>
          ${date ? `<p>${t("bottlenecks.map.popupTime")} ${date}</p>` : ""}
      </div>
    `;

    hoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(map);
  });

  map.on("mousemove", "bottlenecks-layer", (e) => {
    if (hoverPopup.isOpen()) hoverPopup.setLngLat(e.lngLat);
  });

  map.on("mouseleave", "bottlenecks-layer", () => {
    map.getCanvas().style.cursor = "";
    hoverPopup.remove();

    if (hoveredId !== null) {
      map.setFeatureState(
        { source: "bottlenecks-source", id: hoveredId },
        { hover: false }
      );
      hoveredId = null;
    }
  });

  // Click on line → toggle popup
  map.on("click", "bottlenecks-layer", (e) => {
    const feature = e.features && e.features[0];
    if (!feature) return;

    const id = feature.id;

    // If clicking the same feature again → close popup & clear state
    if (clickedId === id) {
      map.setFeatureState(
        { source: "bottlenecks-source", id },
        { click: false }
      );
      clickedId = null;
      clickPopup.remove();
      return;
    }

    // Clear previous clicked feature if different
    if (clickedId !== null && clickedId !== id) {
      map.setFeatureState(
        { source: "bottlenecks-source", id: clickedId },
        { click: false }
      );
    }

    clickedId = id;

    map.setFeatureState({ source: "bottlenecks-source", id }, { click: true });

    const props = feature.properties || {};
    const label = props.label || "";
    const date = props.date || null;


    const html = `
      <div class="custom-popup-content">
        <p><strong>${label}</strong></p>
         ${date ? `<p>${t("bottlenecks.map.popupTime")} ${date}</p>` : ""}
      </div>
    `;

    clickPopup.setLngLat(e.lngLat).setHTML(html).addTo(map);
  });

  // Global map click → close popup if click is NOT on bottlenecks-layer
  map.on("click", (e) => {
    if (clickedId === null) return;

    const features = map.queryRenderedFeatures(e.point, {
      layers: ["bottlenecks-layer"],
    });

    // If we did not click on the bottlenecks layer → close
    if (!features.length) {
      map.setFeatureState(
        { source: "bottlenecks-source", id: clickedId },
        { click: false }
      );
      clickedId = null;
      clickPopup.remove();
    }
  });

  // X button on popup → also clear state
  clickPopup.on("close", () => {
    if (clickedId !== null) {
      map.setFeatureState(
        { source: "bottlenecks-source", id: clickedId },
        { click: false }
      );
      clickedId = null;
    }
  });
}

// Source + layer setup
function addBottlenecksSourceAndLayer(map, geojson, t) {
  let createdLayer = false;

  if (!map.getSource("bottlenecks-source")) {
    map.addSource("bottlenecks-source", {
      type: "geojson",
      data: geojson,
      promoteId: "path_id",
    });
  } else {
    map.getSource("bottlenecks-source").setData(geojson);
  }

  if (!map.getLayer("bottlenecks-layer")) {
    map.addLayer({
      id: "bottlenecks-layer",
      type: "line",
      source: "bottlenecks-source",
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-emissive-strength": 1.0,
        "line-color": ["get", "color"], // from properties.color
        "line-width": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          7,
          ["boolean", ["feature-state", "click"], false],
          7,
          4,
        ],
        "line-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.9,
          ["boolean", ["feature-state", "click"], false],
          0.9,
          0.6,
        ],
      },
    });

    createdLayer = true;
    addBottlenecksInteractivity(map, t);
  } else {
    map.getSource("bottlenecks-source").setData(geojson);
  }

  if (createdLayer) {
    const bounds = computeBounds(geojson);
    if (bounds) {
      map.fitBounds(bounds, { padding: 40, duration: 800 });
    }
  }
}

function BottlenecksMap({ geojson }) {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const map3D = config.MAP_3D;
  const map2D = "mapbox://styles/mapbox/dark-v11";

  const [selectedMapStyle, setSelectedMapStyle] = useState(map3D);

  // Init map (once)
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: selectedMapStyle,
      center: [22.9527, 40.6253],
      zoom: 11,
      projection: "mercator",
    });

    mapRef.current = map;

    map.once("load", () => {
      if (geojson) {
        addBottlenecksSourceAndLayer(map, geojson, t);
      }
    });

    return () => {
      map.remove();
    };
  }, []);

  // Update data when geojson changes (e.g. every 5-minute refresh)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geojson) return;

    if (map.isStyleLoaded()) {
      addBottlenecksSourceAndLayer(map, geojson, t);
    } else {
      map.once("load", () => {
        addBottlenecksSourceAndLayer(map, geojson, t);
      });
    }
  }, [geojson]);

  // Toggle 3D/2D
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setStyle(selectedMapStyle);

    map.once("styledata", (e) => {
      if (e.dataType !== "style") return;

      if (geojson) {
        addBottlenecksSourceAndLayer(map, geojson, t);
      }
    });
  }, [selectedMapStyle]);

  const handleToggle3D = () => {
    setSelectedMapStyle((prev) => (prev === map3D ? map2D : map3D));
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        m: 0,
        p: 0,
      }}
    >
      {/* 3D Toggle */}
      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={selectedMapStyle === map3D}
              onChange={handleToggle3D}
            />
          }
          label={selectedMapStyle === map3D ? t("bottlenecks.map.toggle3dOn") : t("bottlenecks.map.toggle3dOff")}
        />
      </Box>

      {/* Legend */}
      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        <Box sx={{ mt: 1 }}>
          <MDTypography variant="subtitle2">
            {t("bottlenecks.map.legendTitle")}
          </MDTypography>

          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {bottleneckClasses(t).map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    bgcolor: item.color,
                  }}
                />

                <MDTypography variant="body2">{item.label}</MDTypography>
              </Box>
            ))}
          </Box>
        </Box>
      </div>

      {/* Fullscreen map container */}
      <Box
        ref={mapContainerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          m: 0,
          p: 0,
        }}
      />
    </Box>
  );
}

export default BottlenecksMap;
